import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ClipboardList, Receipt, DollarSign, ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const $ = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OpsDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, o, i] = await Promise.all([
          api.get("/ops/summary"),
          api.get("/orders"),
          api.get("/invoices"),
        ]);
        setSummary(s.data);
        setRecentOrders(o.data.slice(0, 5));
        setRecentInvoices(i.data.slice(0, 5));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !summary) {
    return (
      <div className="p-10 text-white/60 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-10 max-w-6xl">
      <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Operaciones</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold">Resumen del negocio</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        <Card to="/admin/ops/quotes" icon={Inbox} label="Nuevas solicitudes" value={summary.quotes.new} sub={`de ${summary.quotes.total} totales`} />
        <Card to="/admin/ops/orders" icon={ClipboardList} label="Pedidos activos" value={summary.orders.active} sub={`de ${summary.orders.total} totales`} />
        <Card to="/admin/ops/invoices" icon={Receipt} label="Facturas pendientes" value={summary.invoices.unpaid} sub={`de ${summary.invoices.total} totales`} />
        <Card to="/admin/ops/invoices" icon={DollarSign} label="Ingresos cobrados" value={$(summary.revenue.paid)} sub={`Por cobrar: ${$(summary.revenue.outstanding)}`} />
      </div>

      <div className="mt-12 grid lg:grid-cols-2 gap-8">
        <RecentList
          title="Últimos pedidos"
          empty="Aún no hay pedidos."
          to="/admin/ops/orders"
          items={recentOrders.map((o) => ({
            id: o.id,
            href: `/admin/ops/orders/${o.id}`,
            primary: o.customer?.full_name || "Sin nombre",
            secondary: o.service,
            badge: o.status,
          }))}
        />
        <RecentList
          title="Últimas facturas"
          empty="Aún no hay facturas."
          to="/admin/ops/invoices"
          items={recentInvoices.map((i) => ({
            id: i.id,
            href: `/admin/ops/invoices/${i.id}`,
            primary: `${i.number} · ${i.customer?.full_name || ""}`,
            secondary: $(i.total),
            badge: i.status,
          }))}
        />
      </div>
    </div>
  );
}

function Card({ to, icon: Icon, label, value, sub }) {
  return (
    <Link
      to={to}
      className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:border-[#D4AF37]/40 transition-all block"
    >
      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
      </div>
      <div className="font-display text-2xl font-bold">{value}</div>
      <div className="text-xs text-white/60 uppercase tracking-wider mt-1">{label}</div>
      {sub && <div className="text-[11px] text-white/40 mt-1">{sub}</div>}
    </Link>
  );
}

function RecentList({ title, items, to, empty }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold">{title}</h3>
        <Link to={to} className="text-xs text-[#D4AF37] hover:underline inline-flex items-center gap-1">
          Ver todos <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 text-center text-sm text-white/55">{empty}</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <Link key={it.id} to={it.href} className="block bg-white/[0.04] border border-white/10 rounded-xl p-4 hover:border-[#D4AF37]/40 transition-all">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{it.primary}</div>
                  <div className="text-xs text-white/55 truncate">{it.secondary}</div>
                </div>
                <StatusPill value={it.badge} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function StatusPill({ value }) {
  const map = {
    new: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    converted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    archived: "bg-white/10 text-white/50 border-white/20",
    pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    scheduled: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    in_progress: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
    completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
    draft: "bg-white/10 text-white/60 border-white/20",
    sent: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    overdue: "bg-red-500/15 text-red-300 border-red-500/30",
  };
  const cls = map[value] || "bg-white/10 text-white/60 border-white/20";
  const label = (value || "—").replace(/_/g, " ");
  return (
    <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold border ${cls}`}>
      {label}
    </span>
  );
}
